import { useState } from "react";
import type { CampoCliente } from "@juegossc/shared";

type Props = {
  camposVisibles: CampoCliente[];
  camposObligatorios: CampoCliente[];
  onSubmit: (
    datos: Partial<Record<CampoCliente, string>>,
  ) => void | Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
};

const LABELS: Record<CampoCliente, { label: string; placeholder: string; type: string; inputMode?: string }> = {
  nombre: { label: "Nombre completo", placeholder: "Juan Pérez", type: "text" },
  telefono: { label: "Teléfono", placeholder: "0976 123 456", type: "tel", inputMode: "tel" },
  cedula: { label: "Cédula", placeholder: "1.234.567", type: "text", inputMode: "numeric" },
  email: { label: "Email", placeholder: "tu@email.com", type: "email", inputMode: "email" },
  ticket: { label: "Nº de ticket", placeholder: "12345", type: "text", inputMode: "numeric" },
};

export function DatosClienteForm({
  camposVisibles,
  camposObligatorios,
  onSubmit,
  onCancel,
  loading,
}: Props) {
  const [datos, setDatos] = useState<Partial<Record<CampoCliente, string>>>({});
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const faltantes = camposObligatorios.filter(
      (k) => !datos[k] || datos[k]!.trim() === "",
    );
    if (faltantes.length > 0) {
      setError(
        `Completá: ${faltantes.map((k) => LABELS[k].label).join(", ")}`,
      );
      return;
    }
    setError(null);
    void onSubmit(datos);
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <h2 className="text-2xl font-bold">Cargá tus datos antes de jugar</h2>
      <p className="text-sm opacity-70">
        Necesarios para registrar tu participación.
      </p>

      {camposVisibles.map((campo) => {
        const cfg = LABELS[campo];
        const required = camposObligatorios.includes(campo);
        return (
          <label key={campo} className="flex flex-col gap-1 text-sm">
            <span className="opacity-80">
              {cfg.label}
              {required && <span className="text-red-400"> *</span>}
            </span>
            <input
              type={cfg.type}
              inputMode={cfg.inputMode as React.HTMLAttributes<HTMLInputElement>["inputMode"]}
              value={datos[campo] ?? ""}
              onChange={(e) =>
                setDatos((d) => ({ ...d, [campo]: e.target.value }))
              }
              placeholder={cfg.placeholder}
              className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
              required={required}
            />
          </label>
        );
      })}

      {error && (
        <div className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-lg bg-white/10 py-3 font-bold hover:bg-white/20"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="flex-1 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold disabled:opacity-50"
        >
          {loading ? "Guardando…" : "Confirmar y jugar"}
        </button>
      </div>
    </form>
  );
}
